import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  adminWorkspaceConfigs,
  getAdminWorkspaceConfigsQueryKey
} from '@r/api/generated/client.ts';
import { Card, CardContent } from '@ui/card.tsx';
import { Separator } from '@ui/separator.tsx';
import { findConfig, getConfigsFromResponse, useAllConfigs } from '@r/hooks/use-all-configs.ts';
import { useOrvalErrorToast, useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import {
  MissingPurchaseProofConfigError,
  MissingWaitingListConfigError,
  savePurchaseProofSetting,
  saveWaitingListSetting
} from './purchase-proof-settings-api.ts';
import { isPurchaseProofEnabled, isWaitingListEnabled } from './purchase-proof-settings-utils.ts';
import { FeatureSettingsSection, FeaturesIntroduction } from './feature-settings-content.tsx';

const PURCHASE_PROOF_CONFIG_KEY = 'enable-purchase-proof';
const WAITING_LIST_CONFIG_KEY = 'accept-waiting-list';
const purchaseProofConfigParams = {
  'config.type': 'core',
  'config.key': PURCHASE_PROOF_CONFIG_KEY,
  perPage: 1
};
const waitingListConfigParams = {
  'config.type': 'core',
  'config.key': WAITING_LIST_CONFIG_KEY,
  perPage: 1
};

/** Displays and manages the workspace feature settings available to administrators. */
export function PurchaseProofSettings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const allConfigsQuery = useAllConfigs();
  const configs = getConfigsFromResponse(allConfigsQuery.data);
  const purchaseProofConfigDefinition = findConfig(configs, 'core', PURCHASE_PROOF_CONFIG_KEY);
  const waitingListConfigDefinition = findConfig(configs, 'core', WAITING_LIST_CONFIG_KEY);

  const purchaseProofConfigQuery = useQuery({
    queryKey: getAdminWorkspaceConfigsQueryKey(purchaseProofConfigParams),
    queryFn: () => adminWorkspaceConfigs(purchaseProofConfigParams)
  });
  const waitingListConfigQuery = useQuery({
    queryKey: getAdminWorkspaceConfigsQueryKey(waitingListConfigParams),
    queryFn: () => adminWorkspaceConfigs(waitingListConfigParams)
  });
  const purchaseProofWorkspaceConfig = purchaseProofConfigQuery.data?.data?.[0];
  const waitingListWorkspaceConfig = waitingListConfigQuery.data?.data?.[0];
  const purchaseProofEnabled = isPurchaseProofEnabled(purchaseProofWorkspaceConfig?.value);
  const waitingListEnabled = isWaitingListEnabled(waitingListWorkspaceConfig?.value);

  useOrvalQueryErrorToast(
    {
      isError: allConfigsQuery.isError,
      error: allConfigsQuery.error,
      errorUpdatedAt: allConfigsQuery.errorUpdatedAt
    },
    t('settings.purchase.proofs.load.error')
  );
  useOrvalQueryErrorToast(
    {
      isError: purchaseProofConfigQuery.isError,
      error: purchaseProofConfigQuery.error,
      errorUpdatedAt: purchaseProofConfigQuery.errorUpdatedAt
    },
    t('settings.purchase.proofs.load.error')
  );
  useOrvalQueryErrorToast(
    {
      isError: waitingListConfigQuery.isError,
      error: waitingListConfigQuery.error,
      errorUpdatedAt: waitingListConfigQuery.errorUpdatedAt
    },
    t('settings.waiting.list.load.error')
  );

  const updatePurchaseProofSetting = useMutation({
    mutationFn: (enabled: boolean) =>
      savePurchaseProofSetting({
        enabled,
        configDefinitionId: purchaseProofConfigDefinition?.id,
        workspaceConfigId: purchaseProofWorkspaceConfig?.id
      }),
    onSuccess: async (_data, enabled) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getAdminWorkspaceConfigsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] }),
        queryClient.invalidateQueries({ queryKey: ['availableRoutes'] })
      ]);
      toast.success(
        enabled ? t('settings.purchase.proofs.enabled') : t('settings.purchase.proofs.disabled')
      );
    },
    onError: (error) => {
      if (error instanceof MissingPurchaseProofConfigError) {
        toast.error(t('settings.purchase.proofs.missing.config'));
        return;
      }

      showOrvalErrorToast(error, t('settings.purchase.proofs.update.error'));
    }
  });

  const updateWaitingListSetting = useMutation({
    mutationFn: (enabled: boolean) =>
      saveWaitingListSetting({
        enabled,
        configDefinitionId: waitingListConfigDefinition?.id,
        workspaceConfigId: waitingListWorkspaceConfig?.id
      }),
    onSuccess: async (_data, enabled) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getAdminWorkspaceConfigsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] }),
        queryClient.invalidateQueries({ queryKey: ['availableRoutes'] })
      ]);
      toast.success(
        enabled
          ? t('settings.waiting.list.enable.success')
          : t('settings.waiting.list.disable.success')
      );
    },
    onError: (error) => {
      if (error instanceof MissingWaitingListConfigError) {
        toast.error(t('settings.waiting.list.missing.config'));
        return;
      }

      showOrvalErrorToast(error, t('settings.waiting.list.update.error'));
    }
  });

  /** Applies or confirms a purchase-proof feature state change. */
  const handlePurchaseProofChange = (enabled: boolean) => {
    if (enabled) {
      updatePurchaseProofSetting.mutate(true);
      return;
    }

    toast(t('settings.purchase.proofs.disable.confirm'), {
      action: {
        label: t('label.confirm'),
        onClick: () => updatePurchaseProofSetting.mutate(false)
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  /** Applies or confirms a waiting-list feature state change. */
  const handleWaitingListChange = (enabled: boolean) => {
    if (enabled) {
      updateWaitingListSetting.mutate(true);
      return;
    }

    toast(t('settings.waiting.list.disable.confirm'), {
      action: {
        label: t('label.confirm'),
        onClick: () => updateWaitingListSetting.mutate(false)
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  const purchaseProofLoading = allConfigsQuery.isPending || purchaseProofConfigQuery.isPending;
  const waitingListLoading = allConfigsQuery.isPending || waitingListConfigQuery.isPending;
  const displayedPurchaseProofValue =
    updatePurchaseProofSetting.isPending && updatePurchaseProofSetting.variables !== undefined
      ? updatePurchaseProofSetting.variables
      : purchaseProofEnabled;
  const displayedWaitingListValue =
    updateWaitingListSetting.isPending && updateWaitingListSetting.variables !== undefined
      ? updateWaitingListSetting.variables
      : waitingListEnabled;

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('settings.features.title')}</h1>
        <p className="font-light text-muted-foreground">{t('settings.features.description')}</p>
      </div>

      <Card className="w-full">
        <CardContent className="space-y-8 p-8">
          <FeaturesIntroduction />

          <FeatureSettingsSection
            title={t('settings.purchase.proofs.title')}
            description={t('settings.purchase.proofs.description')}
            toggleLabel={t('settings.purchase.proofs.toggle.label')}
            toggleAriaLabel={t('settings.purchase.proofs.toggle.aria')}
            checked={displayedPurchaseProofValue}
            isLoading={purchaseProofLoading}
            disabled={
              updatePurchaseProofSetting.isPending ||
              purchaseProofConfigQuery.isError ||
              allConfigsQuery.isError ||
              !purchaseProofConfigDefinition?.id
            }
            onCheckedChange={handlePurchaseProofChange}
          />

          <Separator />

          <FeatureSettingsSection
            title={t('settings.waiting.list.title')}
            description={t('settings.waiting.list.description')}
            toggleLabel={t('settings.waiting.list.toggle.label')}
            toggleAriaLabel={t('settings.waiting.list.toggle.aria')}
            checked={displayedWaitingListValue}
            isLoading={waitingListLoading}
            disabled={
              updateWaitingListSetting.isPending ||
              waitingListConfigQuery.isError ||
              allConfigsQuery.isError ||
              !waitingListConfigDefinition?.id
            }
            onCheckedChange={handleWaitingListChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
